<template>
  <div class="row">
    <div class="col-md-12">
      <div class="ibox">
        <div class="ibox-content p-t-m">
          <div class="row m-t-m">
            <div class="col-md-4">
              <h1 class="form-section-title">Private Lesson Pricing For</h1>
            </div>
            <div class="col-md-8">
              <div class="form-group">
                <label for="coach_name" class="m-b-none sr-only">Coach Name</label>
                <div class="flex align-items-start gap-sm">
                  <img v-if="coachProfile.avatar" :src="coachProfile.avatar" class="profile-pic w-[100px] h-[100px] object-cover rounded-[8px]" alt="Coach avatar">
                  <img v-else src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect x='0' y='0' width='40' height='40' rx='20' fill='%23E5E7EB'/%3E%3C/svg%3E" class="rounded-circle" width="40" height="40" alt="Coach avatar placeholder">
                  <p class="text-gray-600 m-b-none">{{ coachProfile.full_name }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="row m-t-xl">
            <div class="col-md-4">
              <h1 class="form-section-title">Available Private Lesson Products</h1>
              <p class="m-b-none">Select which private lesson products this coach can offer.</p>
              <div class="m-t-md flex flex-col gap-sm p-m bg-gray-50 border-solid border border-gray-200 rounded-xs max-w-xl text-center align-items-center">
                <button class="btn btn-primary btn-block w-full" @click="openProductCreation">
                  <i class="fa fa-plus m-r-xs"></i> Create Private Lesson Product
                </button>
                <h3 class="m-y-none">Not seeing anything? Create a private lesson product first.</h3>
              </div>
            </div>
            <div class="col-md-8">
              <label class="m-b-sm">Selected pricing. These are available to clients when booking private lessons.</label>
              <p v-if="coachProfile.product_coach_listings.length === 0">No private lessons selected yet.</p>
              <div class="grid grid-cols-5 sm-grid-cols-1 md-grid-cols-2 gap-sm m-b-sm">
                <div v-for="listing in coachProfile.product_coach_listings" :key="listing.product_id" @click.prevent="toggleProductListing(listing.product)"
                     v-if="isSelected(listing.product)"
                >
                  <product-list-result :product="listing.product" :show-edit="false"></product-list-result>
                </div>
              </div>
              <hr/>
              <label class="m-b-sm">Available private lesson products. Tap to add to {{ coachProfile.full_name }}.</label>
              <filtered-list
                  ui="grid"
                  grid-classes="grid grid-cols-5 sm-grid-cols-1 md-grid-cols-2 gap-sm m-b-sm"
                  :headers="['Name', 'Category', 'Actions']"
                  :sorts="[
                    {
                      label: 'Name asc',
                      value: 'name asc'
                    },
                    {
                      label: 'Name desc',
                      value: 'name desc'
                    }
                  ]"
                  :filters="[
                    {
                      label: 'Category',
                      attribute: 'category',
                      type: 'primary',
                      predicates: [
                        {
                          label: 'Private Lessons',
                          user_editable: false,
                          default: true,
                          values: {
                            category_eq: 'private_lesson'
                          }
                        }
                      ]
                    },
                    {
                      label: 'Name',
                      predicateLabel: 'Filter by product name',
                      attribute: 'name',
                      type: 'secondary',
                      predicates: [
                        {
                          id: 1,
                          label: 'Name contains',
                          type: 'TextContains',
                          user_editable: true,
                          operator: 'or',
                          placeholder: 'Product name',
                          values: {
                            name_cont_any: null
                          }
                        }
                      ]
                    }
                  ]"
                  response-key="products"
                  object-type="Product"
                  base-url="/api/v1/products">
                <template v-slot:row="row">
                  <div @click.prevent="toggleProductListing(row.data)" v-if="notSelected(row.data)">
                    <product-list-result :product="row.data" :show-edit="false"></product-list-result>
                  </div>
                </template>
              </filtered-list>
            </div>
          </div>
          <div class="button-area text-right m-t-xl">
            <div>
              <ladda-button @lbClicked="saveCoachLessons" el-class="btn-primary" :loading="loading">
                Save
              </ladda-button>
              <div v-if="error">
                <small>
                  <span class="text-danger">{{ errorMessage }}</span>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errorableMix } from './mixins/errorable_mix';
import Spinner from './spinner.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import ProductListResult from './product_list_result.vue';
import FilteredList from './filtered_list.vue';

export default {
  name: 'coach-private-lessons-pricing-form',
  mixins: [errorableMix],
  components: {
    LaddaButton,
    Spinner,
    FilteredList,
    ProductListResult,
  },
  props: {
    existingCoachProfile: {
      type: Object,
      required: true
    },
    returnToUrl: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      coachProfile: {
        id: null,
        name: '',
        product_coach_listings: [],
      },
      loading: false,
    }
  },
  computed: {
    listingParams() {
      return this.coachProfile.product_coach_listings.map(listing => {
        return {
          id: listing.id,
          product_id: listing.product_id,
          _destroy: listing._destroy,
        }
      });
    },
  },
  mounted() {
    if (this.existingCoachProfile) {
      this.coachProfile = this.existingCoachProfile;
    }
  },
  methods: {
    isSelected(product) {
      return !this.notSelected(product);
    },
    notSelected(product) {
      let listing = this.coachProfile.product_coach_listings.find(listing => listing.product_id === product.id);
      if (listing) {
        return listing._destroy;
      }
      return true;
    },
    toggleProductListing(product) {
      const existingListing = this.coachProfile.product_coach_listings.find(listing => listing.product_id === product.id);
      if (existingListing) {
        if (!existingListing.id) {
          // If this is just a draft index, never saved server side, just remove it from the array
          const draftIdx = _.findIndex(this.coachProfile.product_coach_listings, listing => listing.product_id === product.id);
          this.coachProfile.product_coach_listings.splice(draftIdx, 1);
        } else {
          this.$set(existingListing, '_destroy', !existingListing._destroy);
        }
      } else {
        this.coachProfile.product_coach_listings.push({
          id: null,
          product_id: product.id,
          product: product, // for front end magic
        });
      }
    },
    saveCoachLessons: _.throttle(function() {
      if (this.loading) {
        return;
      }

      this.loading = true;
      const params = {
        coach_profile: {
          product_coach_listings_attributes: this.listingParams,
        }
      }
      this.update(params);
    }, 500),
    update(params) {
      const url = this.$apiService.coachUrl(this.coachProfile.id);
      axios.put(url, params)
          .then(response => {
            this.loading = false;
            this.$notificationManager.$emit('show-toast', 'Saved', true);
            if (this.returnToUrl) {
              window.location.assign(this.returnToUrl);
            } else {
              window.location.assign('/private_lessons/dashboard');
            }
          })
          .catch(error => {
            this.loading = false;
            this.errorMessage = this.parseErrorResponse(error);
            this.error = true;
          });
    },
    openProductCreation() {
      window.location.assign(`/products/new?category=private_lesson&return_to=coach_private_lessons_pricing&return_to_coach_id=${this.coachProfile.id}`);
    },
  }
};
</script>

<style scoped>
</style>
